import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { rupee } from "@/lib/constants";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function AdminServices() {
    const [tab, setTab] = useState("services");
    const [services, setServices] = useState(null);
    const [areas, setAreas] = useState(null);

    const load = () => {
        api.get("/services?all=true").then((r) => setServices(r.data)).catch(() => setServices([]));
        api.get("/service-areas?all=true").then((r) => setAreas(r.data)).catch(() => setAreas([]));
    };
    useEffect(() => { load(); }, []);

    const toggleService = async (s) => {
        try { await api.patch(`/admin/services/${s.id}`, { enabled: !s.enabled }); toast.success(`${s.name} ${!s.enabled ? "enabled" : "disabled"}`); load(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };
    const saveVisitFee = async (s, fee) => {
        try { await api.patch(`/admin/services/${s.id}`, { visit_fee: Number(fee) }); toast.success(`${s.name} visit fee updated`); load(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };
    const toggleArea = async (a) => {
        try { await api.patch(`/admin/service-areas/${a.id}`, { enabled: !a.enabled }); toast.success(`${a.name} ${!a.enabled ? "enabled" : "disabled"}`); load(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };

    return (
        <div>
            <h1 className="font-heading text-2xl font-extrabold text-navy">Services & Areas</h1>
            <p className="text-sm text-navy/55">Enable or disable what HOMIIE offers</p>
            <div className="mt-4 inline-flex rounded-full bg-navy/5 p-1">
                {["services", "areas"].map((t) => (
                    <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)} className={`rounded-full px-5 py-2 text-sm font-bold capitalize transition-colors ${tab === t ? "bg-orange text-white" : "text-navy/60"}`}>{t}</button>
                ))}
            </div>

            {tab === "services" && (
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {services === null && <Loader2 className="h-7 w-7 animate-spin text-orange" />}
                    {services?.map((s) => (
                        <div key={s.id} className="rounded-2xl border border-navy/10 bg-white p-4" data-testid={`service-row-${s.category}`}>
                            <div className="flex items-start justify-between">
                                <div><h3 className="font-heading font-bold text-navy">{s.name}</h3><p className="text-xs text-navy/50">{s.price_label}</p></div>
                                <button data-testid={`toggle-service-${s.category}`} onClick={() => toggleService(s)} className={`relative h-6 w-11 rounded-full transition-colors ${s.enabled ? "bg-orange" : "bg-navy/20"}`}>
                                    <span className={`absolute left-0 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${s.enabled ? "translate-x-[22px]" : "translate-x-0.5"}`} />
                                </button>
                            </div>
                            {s.base_price > 0 && <p className="mt-2 text-sm font-semibold text-navy">{rupee(s.base_price)}</p>}
                            <VisitFeeEditor s={s} onSave={saveVisitFee} />
                        </div>
                    ))}
                </div>
            )}

            {tab === "areas" && (
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {areas === null && <Loader2 className="h-7 w-7 animate-spin text-orange" />}
                    {areas?.map((a) => (
                        <div key={a.id} className="rounded-2xl border border-navy/10 bg-white p-4" data-testid={`area-row-${a.name.replace(/[^a-z]/gi, "").toLowerCase()}`}>
                            <div className="flex items-start justify-between">
                                <div><h3 className="font-heading font-bold text-navy">{a.name}</h3>{a.campaign_code && <p className="text-xs font-semibold text-orange-dark">{a.campaign_code}</p>}</div>
                                <button data-testid={`toggle-area-${a.name.replace(/[^a-z]/gi, "").toLowerCase()}`} onClick={() => toggleArea(a)} className={`relative h-6 w-11 rounded-full transition-colors ${a.enabled ? "bg-orange" : "bg-navy/20"}`}>
                                    <span className={`absolute left-0 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${a.enabled ? "translate-x-[22px]" : "translate-x-0.5"}`} />
                                </button>
                            </div>
                            <AreaGeoEditor a={a} onSaved={load} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function AreaGeoEditor({ a, onSaved }) {
    const slug = a.name.replace(/[^a-z]/gi, "").toLowerCase();
    const [f, setF] = useState({ pincodes: (a.pincodes || []).join(", "), lat: a.lat ?? "", lng: a.lng ?? "", radius_km: a.radius_km ?? "" });
    const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
    const num = (v) => (v === "" ? null : Number(v));
    const save = async () => {
        const body = { pincodes: f.pincodes.split(/[\s,]+/).filter((p) => /^\d{6}$/.test(p)), lat: num(f.lat), lng: num(f.lng), radius_km: num(f.radius_km) };
        try { await api.patch(`/admin/service-areas/${a.id}`, body); toast.success(`${a.name} geofence saved`); onSaved(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };
    const cls = "w-full rounded-lg border border-navy/15 px-2.5 py-1.5 text-xs text-navy outline-none focus:border-orange";
    return (
        <div className="mt-3 space-y-2 border-t border-navy/5 pt-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-navy/40">Geofence (pincodes + radius)</p>
            <input data-testid={`area-pincodes-${slug}`} value={f.pincodes} onChange={set("pincodes")} placeholder="Pincodes, e.g. 342008, 342014" className={cls} />
            <div className="grid grid-cols-3 gap-2">
                <label className="text-[10px] font-semibold text-navy/45">Centre lat<input data-testid={`area-lat-${slug}`} value={f.lat} onChange={set("lat")} placeholder="26.25" className={cls} /></label>
                <label className="text-[10px] font-semibold text-navy/45">Centre lng<input data-testid={`area-lng-${slug}`} value={f.lng} onChange={set("lng")} placeholder="72.97" className={cls} /></label>
                <label className="text-[10px] font-semibold text-navy/45">Radius (km)<input data-testid={`area-radius-${slug}`} value={f.radius_km} onChange={set("radius_km")} placeholder="3.5" className={cls} /></label>
            </div>
            <button data-testid={`area-geo-save-${slug}`} onClick={save} className="rounded-full bg-navy px-3.5 py-1.5 text-xs font-bold text-white active:scale-95">Save geofence</button>
        </div>
    );
}

function VisitFeeEditor({ s, onSave }) {
    const [fee, setFee] = useState(s.visit_fee ?? "");
    return (
        <div className="mt-3 flex items-center gap-2 border-t border-navy/10 pt-3">
            <label className="text-xs font-semibold text-navy/60">Visit fee ₹</label>
            <input data-testid={`visit-fee-${s.category}`} type="number" value={fee} onChange={(e) => setFee(e.target.value)} className="w-20 rounded-lg border border-navy/15 px-2 py-1 text-sm outline-none focus:border-orange" />
            <button data-testid={`save-visit-fee-${s.category}`} onClick={() => onSave(s, fee)} className="rounded-full bg-navy px-3 py-1 text-xs font-bold text-white">Save</button>
        </div>
    );
}
